import { Link } from "react-router-dom";
import { User, ShoppingCart, LayoutDashboard, Shield, LogOut, Globe } from "lucide-react";

const LOGO_URL = "/api/uploads/brand/logo.png";

/**
 * Navbar sub-components — extracted from Navbar.jsx to reduce complexity.
 * No behavior changes; every data-testid preserved.
 */

export function NavLinksDesktop({ navLinks, pathname }) {
  return (
    <div className="flex items-center gap-2">
      {navLinks.map((link) => {
        const isActive = pathname === link.href || pathname.startsWith(link.href + "/");
        return (
          <Link
            key={link.href}
            to={link.href}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
              isActive
                ? "bg-[#00AEEF]/15 text-[#00AEEF] border border-[#00AEEF]/30"
                : "text-slate-300 hover:text-white hover:bg-white/5"
            }`}
            data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
          >
            <link.icon className="w-4 h-4" />
            {link.label}
          </Link>
        );
      })}
    </div>
  );
}

export function LanguageToggle({ lang, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-1.5 px-3 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-all text-xs font-semibold"
      data-testid="lang-toggle-nav"
      title={lang === "en" ? "Cambiar a Español" : "Switch to English"}
    >
      <Globe className="w-4 h-4" />
      {lang === "en" ? "ES" : "EN"}
    </button>
  );
}

export function CartButton({ cartCount }) {
  return (
    <Link
      to="/cart"
      className="relative p-2.5 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-all"
      data-testid="cart-link"
    >
      <ShoppingCart className="w-5 h-5" />
      {cartCount > 0 && (
        <span
          className="absolute -top-1 -right-1 w-5 h-5 bg-[#00AEEF] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          data-testid="cart-count"
        >
          {cartCount}
        </span>
      )}
    </Link>
  );
}

export function UserMenuDesktop({ user, t, onLogout }) {
  if (!user) {
    return (
      <Link
        to="/login"
        className="hidden lg:flex items-center gap-2 px-5 py-2.5 bg-[#00AEEF] hover:bg-[#1AA8FF] text-white text-sm font-semibold rounded-full transition-all duration-200 shadow-lg shadow-[#00AEEF]/25"
        data-testid="login-link"
      >
        <User className="w-4 h-4" />
        {t("nav_login")}
      </Link>
    );
  }

  return (
    <div className="hidden lg:flex items-center gap-1">
      {user.is_admin && (
        <Link
          to="/admin"
          className="p-2.5 text-[#00AEEF] hover:bg-[#00AEEF]/10 rounded-full transition-all"
          data-testid="admin-link"
          title={t("nav_admin_panel")}
        >
          <Shield className="w-5 h-5" />
        </Link>
      )}
      <Link
        to="/dashboard"
        className="p-2.5 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-all"
        data-testid="dashboard-link"
        title={t("nav_dashboard")}
      >
        <LayoutDashboard className="w-5 h-5" />
      </Link>
      <button
        onClick={onLogout}
        className="p-2.5 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-all"
        data-testid="logout-btn"
        title={t("nav_logout")}
      >
        <LogOut className="w-5 h-5" />
      </button>
    </div>
  );
}

export function MobileNavLinks({ navLinks, pathname }) {
  return (
    <div className="p-6 space-y-2">
      {navLinks.map((link) => {
        const isActive = pathname === link.href;
        return (
          <Link
            key={link.href}
            to={link.href}
            className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${
              isActive
                ? "bg-[#00AEEF]/10 text-[#00AEEF] border border-[#00AEEF]/20"
                : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`}
          >
            <link.icon className="w-5 h-5" />
            <span className="font-medium">{link.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function MobileUserSection({ user, t, onLogout }) {
  if (!user) {
    return (
      <Link
        to="/login"
        className="flex items-center justify-center gap-3 w-full px-6 py-3.5 bg-[#00AEEF] hover:bg-[#1AA8FF] text-white font-semibold rounded-xl transition-all"
      >
        <User className="w-5 h-5" />
        {t("nav_login_register")}
      </Link>
    );
  }
  return (
    <div className="space-y-2">
      {user.is_admin && (
        <Link
          to="/admin"
          className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-[#00AEEF] hover:bg-[#00AEEF]/10 transition-all"
        >
          <Shield className="w-5 h-5" />
          <span className="font-medium">{t("nav_admin_panel")}</span>
        </Link>
      )}
      <Link
        to="/dashboard"
        className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
      >
        <LayoutDashboard className="w-5 h-5" />
        <span className="font-medium">{t("nav_dashboard")}</span>
      </Link>
      <button
        onClick={onLogout}
        className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all w-full"
      >
        <LogOut className="w-5 h-5" />
        <span className="font-medium">{t("nav_logout")}</span>
      </button>
    </div>
  );
}

export { LOGO_URL };
